import { deleteFile } from './storage';
import { 
  sectionsCollection, 
  itemsCollection 
} from './menus';
import { 
  collection,
  doc,
  query, 
  where, 
  getDocs, 
  deleteDoc,
  writeBatch 
} from 'firebase/firestore';
import { db } from './config';
import { extractFileNameFromUrl } from './storage';

/**
 * Utility functions for cleaning up restaurant-related files
 */

/**
 * Delete restaurant logo from Firebase Storage
 * Since all logos are now converted to WebP, we only need to delete the .webp file
 */
export const deleteRestaurantLogo = async (restaurantId: string): Promise<void> => {
  try {
    const logoPath = `restaurants/${restaurantId}/logo.webp`;
    await deleteFile(logoPath);
  } catch (error) {
    // Silently ignore if file doesn't exist
    // This is expected for restaurants that never had a logo
  }
};

/**
 * Delete all menu sections for a restaurant
 */
export const deleteRestaurantMenuSections = async (restaurantId: string): Promise<void> => {
  try {
    const sectionsQuery = query(
      sectionsCollection,
      where('restaurantId', '==', restaurantId)
    );
    const sectionsSnapshot = await getDocs(sectionsQuery);
    
    if (sectionsSnapshot.empty) {
      return; // No sections to delete
    }

    // Use batch delete for better performance
    const batch = writeBatch(db);
    sectionsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    
    await batch.commit();
    console.log(`Deleted ${sectionsSnapshot.docs.length} menu sections for restaurant ${restaurantId}`);
  } catch (error) {
    console.error(`Error deleting menu sections for restaurant ${restaurantId}:`, error);
    throw error;
  }
};

/**
 * Delete all menu item images for a restaurant
 */
export const deleteRestaurantMenuItemImages = async (restaurantId: string): Promise<void> => {
  try {
    console.log(`Starting deletion of menu item images for restaurant: ${restaurantId}`);
    
    // Get all menu items for this restaurant
    const itemsQuery = query(
      itemsCollection,
      where('restaurantId', '==', restaurantId)
    );
    const itemsSnapshot = await getDocs(itemsQuery);
    
    if (itemsSnapshot.empty) {
      console.log(`No menu items found for restaurant ${restaurantId}`);
      return; // No items to delete images for
    }

    console.log(`Found ${itemsSnapshot.docs.length} menu items for restaurant ${restaurantId}`);

    // Extract image URLs and delete them
    const imageDeletePromises = itemsSnapshot.docs.map(async (doc) => {
      const item = doc.data();
      if (item.imageUrl) {
        try {
          console.log(`Deleting image for item: ${item.name}, URL: ${item.imageUrl}`);
          const fileName = extractFileNameFromUrl(item.imageUrl);
          if (fileName) {
            const imagePath = `restaurants/${restaurantId}/menu-items/${fileName}`;
            console.log(`Deleting file at path: ${imagePath}`);
            await deleteFile(imagePath);
            console.log(`Successfully deleted image: ${fileName}`);
          }
        } catch (error) {
          console.warn(`Failed to delete image for item ${item.name}:`, error);
          // Don't fail the entire operation for individual image deletion failures
        }
      }
    });
    
    await Promise.allSettled(imageDeletePromises);
    console.log(`Completed image deletion processing for restaurant ${restaurantId}`);
  } catch (error) {
    console.error(`Error deleting menu item images for restaurant ${restaurantId}:`, error);
    // Don't throw error to avoid blocking restaurant deletion
  }
};
export const deleteRestaurantMenuItems = async (restaurantId: string): Promise<void> => {
  try {
    const itemsQuery = query(
      itemsCollection,
      where('restaurantId', '==', restaurantId)
    );
    const itemsSnapshot = await getDocs(itemsQuery);
    
    if (itemsSnapshot.empty) {
      return; // No items to delete
    }

    // Use batch delete for better performance
    const batch = writeBatch(db);
    itemsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    
    await batch.commit();
    console.log(`Deleted ${itemsSnapshot.docs.length} menu items for restaurant ${restaurantId}`);
  } catch (error) {
    console.error(`Error deleting menu items for restaurant ${restaurantId}:`, error);
    throw error;
  }
};

/**
 * Delete all menu data (sections and items) for a restaurant
 */
export const deleteRestaurantMenuData = async (restaurantId: string): Promise<void> => {
  try {
    // Delete menu items first (they reference sections)
    await deleteRestaurantMenuItems(restaurantId);
    
    // Then delete menu sections
    await deleteRestaurantMenuSections(restaurantId);
    
    console.log(`Successfully deleted all menu data for restaurant ${restaurantId}`);
  } catch (error) {
    console.error(`Error deleting menu data for restaurant ${restaurantId}:`, error);
    throw error;
  }
};

/**
 * Delete all files associated with a restaurant
 * This includes logos, menu item images, and image menu files
 */
export const deleteRestaurantFiles = async (restaurantId: string): Promise<void> => {
  try {
    // Delete restaurant logo
    await deleteRestaurantLogo(restaurantId);
    
    // Delete all menu item images
    await deleteRestaurantMenuItemImages(restaurantId);
    
    // Delete all image menu files
    await deleteRestaurantImageMenuFiles(restaurantId);
    
    console.log(`Successfully deleted files for restaurant ${restaurantId}`);
  } catch (error) {
    console.warn(`Failed to delete some files for restaurant ${restaurantId}:`, error);
    // Don't throw error to avoid blocking restaurant deletion
  }
};

/**
 * Comprehensive cleanup of all restaurant-related data
 * This should be called when a restaurant is deleted
 */
export const deleteRestaurantData = async (restaurantId: string): Promise<void> => {
  try {
    console.log(`Starting comprehensive cleanup for restaurant ${restaurantId}`);
    
    // IMPORTANT: Delete files FIRST (while Firestore documents still exist to read imageUrls)
    await deleteRestaurantFiles(restaurantId);
    
    // THEN delete menu data (sections and items from Firestore)
    await deleteRestaurantMenuData(restaurantId);
    
    // THEN delete image menu data (items from Firestore)
    await deleteRestaurantImageMenuItems(restaurantId);
    
    // FINALLY delete analytics data (menu analytics and restaurant stats)
    await deleteRestaurantAnalytics(restaurantId);
    
    console.log(`Successfully completed cleanup for restaurant ${restaurantId}`);
  } catch (error) {
    console.error(`Error during restaurant cleanup for ${restaurantId}:`, error);
    throw error;
  }
};

/**
 * Utility function to clean up orphaned menu data
 * This can be used to clean up menu sections/items that reference non-existent restaurants
 */
export const cleanupOrphanedMenuData = async (): Promise<void> => {
  try {
    console.log('Starting cleanup of orphaned menu data...');
    
    // Get all restaurant IDs
    const restaurantsQuery = query(collection(db, 'restaurants'));
    const restaurantsSnapshot = await getDocs(restaurantsQuery);
    const validRestaurantIds = new Set(restaurantsSnapshot.docs.map(doc => doc.id));
    
    // Check menu sections for orphaned data
    const sectionsSnapshot = await getDocs(sectionsCollection);
    const orphanedSections = sectionsSnapshot.docs.filter(
      doc => !validRestaurantIds.has(doc.data().restaurantId)
    );
    
    // Check menu items for orphaned data
    const itemsSnapshot = await getDocs(itemsCollection);
    const orphanedItems = itemsSnapshot.docs.filter(
      doc => !validRestaurantIds.has(doc.data().restaurantId)
    );
    
    // Check image menu items for orphaned data
    const imageMenuSnapshot = await getDocs(collection(db, 'imageMenuItems'));
    const orphanedImageMenuItems = imageMenuSnapshot.docs.filter(
      doc => !validRestaurantIds.has(doc.data().restaurantId)
    );
    
    // Check menu analytics for orphaned data
    const analyticsSnapshot = await getDocs(collection(db, 'menuAnalytics'));
    const orphanedAnalytics = analyticsSnapshot.docs.filter(
      doc => !validRestaurantIds.has(doc.data().restaurantId)
    );
    
    // Check restaurant stats for orphaned data
    const statsSnapshot = await getDocs(collection(db, 'restaurantStats'));
    const orphanedStats = statsSnapshot.docs.filter(
      doc => !validRestaurantIds.has(doc.id)
    );
    
    // Delete orphaned data in batches
    if (orphanedSections.length > 0) {
      const batch = writeBatch(db);
      orphanedSections.forEach(doc => batch.delete(doc.ref));
      await batch.commit();
      console.log(`Cleaned up ${orphanedSections.length} orphaned menu sections`);
    }
    
    if (orphanedItems.length > 0) {
      const batch = writeBatch(db);
      orphanedItems.forEach(doc => batch.delete(doc.ref));
      await batch.commit();
      console.log(`Cleaned up ${orphanedItems.length} orphaned menu items`);
    }
    
    if (orphanedImageMenuItems.length > 0) {
      const batch = writeBatch(db);
      orphanedImageMenuItems.forEach(doc => batch.delete(doc.ref));
      await batch.commit();
      console.log(`Cleaned up ${orphanedImageMenuItems.length} orphaned image menu items`);
    }
    
    if (orphanedAnalytics.length > 0) {
      const batch = writeBatch(db);
      orphanedAnalytics.forEach(doc => batch.delete(doc.ref));
      await batch.commit();
      console.log(`Cleaned up ${orphanedAnalytics.length} orphaned menu analytics`);
    }
    
    if (orphanedStats.length > 0) {
      const batch = writeBatch(db);
      orphanedStats.forEach(doc => batch.delete(doc.ref));
      await batch.commit();
      console.log(`Cleaned up ${orphanedStats.length} orphaned restaurant stats`);
    }
    
    console.log('Orphaned menu data cleanup completed');
  } catch (error) {
    console.error('Error cleaning up orphaned menu data:', error);
    throw error;
  }
};

/**
 * Delete all image menu items for a restaurant from Firestore
 */
export const deleteRestaurantImageMenuItems = async (restaurantId: string): Promise<void> => {
  try {
    const imageMenuQuery = query(
      collection(db, 'imageMenuItems'),
      where('restaurantId', '==', restaurantId)
    );
    const imageMenuSnapshot = await getDocs(imageMenuQuery);
    
    if (imageMenuSnapshot.empty) {
      console.log(`No image menu items found for restaurant ${restaurantId}`);
      return; // No items to delete
    }

    // Use batch delete for better performance
    const batch = writeBatch(db);
    imageMenuSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    
    await batch.commit();
    console.log(`Deleted ${imageMenuSnapshot.docs.length} image menu items for restaurant ${restaurantId}`);
  } catch (error) {
    console.error(`Error deleting image menu items for restaurant ${restaurantId}:`, error);
    throw error;
  }
};

/**
 * Delete all image menu files for a restaurant from Firebase Storage
 */
export const deleteRestaurantImageMenuFiles = async (restaurantId: string): Promise<void> => {
  try {
    console.log(`Starting deletion of image menu files for restaurant: ${restaurantId}`);
    
    // Get all image menu items for this restaurant
    const imageMenuQuery = query(
      collection(db, 'imageMenuItems'),
      where('restaurantId', '==', restaurantId)
    );
    const imageMenuSnapshot = await getDocs(imageMenuQuery);
    
    if (imageMenuSnapshot.empty) {
      console.log(`No image menu items found for restaurant ${restaurantId}`);
      return; // No items to delete files for
    }

    console.log(`Found ${imageMenuSnapshot.docs.length} image menu items for restaurant ${restaurantId}`);

    // Extract image URLs and delete them
    const imageDeletePromises = imageMenuSnapshot.docs.map(async (doc) => {
      const item = doc.data();
      
      // Delete main image
      if (item.imageUrl) {
        try {
          console.log(`Deleting main image: ${item.imageUrl}`);
          const fileName = extractFileNameFromUrl(item.imageUrl);
          if (fileName) {
            const imagePath = `restaurants/${restaurantId}/image-menu/${fileName}`;
            console.log(`Deleting main image at path: ${imagePath}`);
            await deleteFile(imagePath);
            console.log(`Successfully deleted main image: ${fileName}`);
          }
        } catch (error) {
          console.warn(`Failed to delete main image for item ${item.fileName}:`, error);
        }
      }
      
      // Delete thumbnail
      if (item.thumbnailUrl) {
        try {
          console.log(`Deleting thumbnail: ${item.thumbnailUrl}`);
          const thumbnailFileName = extractFileNameFromUrl(item.thumbnailUrl);
          if (thumbnailFileName) {
            const thumbnailPath = `restaurants/${restaurantId}/image-menu/thumbnails/${thumbnailFileName}`;
            console.log(`Deleting thumbnail at path: ${thumbnailPath}`);
            await deleteFile(thumbnailPath);
            console.log(`Successfully deleted thumbnail: ${thumbnailFileName}`);
          }
        } catch (error) {
          console.warn(`Failed to delete thumbnail for item ${item.fileName}:`, error);
        }
      }
    });
    
    await Promise.allSettled(imageDeletePromises);
    console.log(`Completed image menu file deletion processing for restaurant ${restaurantId}`);
  } catch (error) {
    console.error(`Error deleting image menu files for restaurant ${restaurantId}:`, error);
    // Don't throw error to avoid blocking restaurant deletion
  }
};

/**
 * Delete all image menu data (items and files) for a restaurant
 */
export const deleteRestaurantImageMenuData = async (restaurantId: string): Promise<void> => {
  try {
    console.log(`Starting image menu cleanup for restaurant ${restaurantId}`);
    
    // IMPORTANT: Delete files FIRST (while Firestore documents still exist to read URLs)
    await deleteRestaurantImageMenuFiles(restaurantId);
    
    // THEN delete image menu items from Firestore
    await deleteRestaurantImageMenuItems(restaurantId);
    
    console.log(`Successfully deleted all image menu data for restaurant ${restaurantId}`);
  } catch (error) {
    console.error(`Error deleting image menu data for restaurant ${restaurantId}:`, error);
    throw error;
  }
};

/**
 * Delete all menu analytics data for a restaurant
 */
export const deleteRestaurantMenuAnalytics = async (restaurantId: string): Promise<void> => {
  try {
    console.log(`Starting menu analytics cleanup for restaurant ${restaurantId}`);
    
    const analyticsQuery = query(
      collection(db, 'menuAnalytics'),
      where('restaurantId', '==', restaurantId)
    );
    const analyticsSnapshot = await getDocs(analyticsQuery);
    
    if (analyticsSnapshot.empty) {
      console.log(`No menu analytics found for restaurant ${restaurantId}`);
      return;
    }

    // Use batch delete for better performance
    const batch = writeBatch(db);
    analyticsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    
    await batch.commit();
    console.log(`Deleted ${analyticsSnapshot.docs.length} menu analytics records for restaurant ${restaurantId}`);
  } catch (error) {
    console.error(`Error deleting menu analytics for restaurant ${restaurantId}:`, error);
    throw error;
  }
};

/**
 * Delete restaurant stats document
 */
export const deleteRestaurantStats = async (restaurantId: string): Promise<void> => {
  try {
    console.log(`Deleting restaurant stats for restaurant ${restaurantId}`);
    
    const statsDocRef = doc(db, 'restaurantStats', restaurantId);
    await deleteDoc(statsDocRef);
    
    console.log(`Successfully deleted restaurant stats for restaurant ${restaurantId}`);
  } catch (error) {
    console.error(`Error deleting restaurant stats for restaurant ${restaurantId}:`, error);
    throw error;
  }
};

/**
 * Delete all analytics data for a restaurant
 * This includes both menu analytics records and restaurant stats
 */
export const deleteRestaurantAnalytics = async (restaurantId: string): Promise<void> => {
  try {
    console.log(`Starting analytics cleanup for restaurant ${restaurantId}`);
    
    // Delete menu analytics records
    await deleteRestaurantMenuAnalytics(restaurantId);
    
    // Delete restaurant stats document
    await deleteRestaurantStats(restaurantId);
    
    console.log(`Successfully deleted all analytics data for restaurant ${restaurantId}`);
  } catch (error) {
    console.error(`Error deleting analytics data for restaurant ${restaurantId}:`, error);
    throw error;
  }
};
